import { ColDef } from "@ag-grid-community/core";
import { inject } from "@angular/core";
import {
  AlgoMode,
  CampaignType,
  Marketplace,
  Strategy,
  StrategyAsin,
  StrategyAsins,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { AnyStrategyUpdateParams } from "@front/m19-models";
import { AccountSelectionService, AsinService, Constant, TacosStrategiesService } from "@front/m19-services";
import { DateUtils } from "@front/m19-utils";
import {
  BulkUploadPreviewStatus,
  BulkUploadRunStatus,
} from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon.component";
import { BulkImportService } from "@m19-board/strategies/strategy-bulk-upload-modal/bulk-import.service";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  forkJoin,
  isObservable,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";
import {
  BulkUploadBuckets,
  BulkUploadFieldPreview,
  BulkUploadHandler,
  BulkUploadProgressCounter,
  BulkUploadRowPreview,
  BulkUploadRunResultItem,
  rowHasFieldError,
  rowIsUnchanged,
} from "../bulk-upload-context.service";
import { BulkUploadGridUtilsService } from "../bulk-upload-grid-utils.service";
import { StrategyFromCT } from "@front/m19-models/strategy/StrategyBuilder";

export type BulkFieldValidator<T> = (entity: Partial<T>) => string | Observable<string | null> | null;

type StrategyBulkConfigField<TStrategy> = {
  field: keyof TStrategy;
  title: string;
  csvField: { header: string };
};

export abstract class StrategyBulkUploadHandler<TStrategy extends Strategy & StrategyAsins>
  implements BulkUploadHandler<TStrategy>
{
  protected readonly bulkImportService = inject(BulkImportService);
  protected readonly asinsService = inject(AsinService);
  protected readonly accountSelectionService = inject(AccountSelectionService);
  protected readonly strategyService = inject(StrategyService);
  protected readonly bulkUploadGridUtilsService = inject(BulkUploadGridUtilsService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);

  protected abstract readonly campaignType: CampaignType.SP | CampaignType.SD;
  protected abstract readonly bulkConfig: ReadonlyArray<StrategyBulkConfigField<TStrategy>>;

  protected readonly catalog$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.asinsService.getCatalog(am.accountId, am.marketplace)),
  );

  protected readonly strategyIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
  );

  public get expectedHeaders(): string[] {
    return this.bulkConfig.map((field) => field.csvField.header);
  }

  private readonly progress = new BehaviorSubject<BulkUploadProgressCounter>({ completed: 0, total: 0 });
  public readonly progress$ = this.progress.asObservable();

  protected abstract buildCreateRequest$(creation: BulkUploadRowPreview<TStrategy>): Observable<TStrategy>;

  protected abstract getFieldValidators(): Partial<Record<keyof TStrategy, BulkFieldValidator<TStrategy>>>;

  protected abstract getCreationFieldValidators(): Partial<Record<keyof TStrategy, BulkFieldValidator<TStrategy>>>;

  protected abstract getUpdateFieldValidators(): Partial<Record<keyof TStrategy, BulkFieldValidator<TStrategy>>>;

  protected extraUpdateParams(
    _parsedEntity: Partial<TStrategy>,
    _existingStrategy: TStrategy,
  ): Partial<AnyStrategyUpdateParams> {
    return {};
  }

  buildPreview(rows: string[]): Observable<BulkUploadBuckets<TStrategy>> {
    if (rows.length === 0) return of([]);
    return forkJoin(rows.map((row) => this.parseRow(row.split(/\t/)))).pipe(
      map((parsedRows) => parsedRows.filter((row): row is BulkUploadRowPreview<TStrategy> => row !== null)),
    );
  }

  run(buckets: BulkUploadBuckets<TStrategy>): Observable<BulkUploadRunResultItem<TStrategy>[]> {
    const filteredCreationRequests = buckets.filter(
      (rowPreview) => rowPreview.status === BulkUploadPreviewStatus.CREATION,
    );
    const filteredUpdateRequests = buckets.filter((rowPreview) => rowPreview.status === BulkUploadPreviewStatus.UPDATE);

    this.setProgressTotals(filteredCreationRequests.length, filteredUpdateRequests.length);

    const allRequests = this.buildCreateRequests(filteredCreationRequests).concat(
      this.buildUpdateRequests(filteredUpdateRequests),
    );
    if (allRequests.length === 0) return of([]);

    return forkJoin(allRequests);
  }

  getPreviewGridColumnsDefs(): ColDef<BulkUploadRowPreview<TStrategy>>[] {
    return this.bulkUploadGridUtilsService.getPreviewGridColumnsDefs(this.bulkConfig, "name");
  }

  getRunResultGridColumnsDefs(): ColDef<BulkUploadRunResultItem<TStrategy>>[] {
    const configWithoutStrategyName = this.bulkConfig.filter((field) => field.field !== "name");
    return this.bulkUploadGridUtilsService.getRunResultGridColumnsDefs(configWithoutStrategyName, "name", "strategyId");
  }

  export(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
        withLatestFrom(this.accountSelectionService.singleAccountMarketplaceSelection$),
      )
      .subscribe(([strategies, am]) => {
        this.bulkImportService.exportStrategyCsv(am, this.campaignType, Array.from(strategies.values()));
      });
  }

  protected validateStrategyIdNumber(entity: Partial<TStrategy>): string | null {
    if (entity.strategyId === undefined || isNaN(entity.strategyId)) return "Strategy ID is not a number or is missing";
    return null;
  }

  protected validateName(entity: Partial<TStrategy>): string | null {
    const name = entity.name;
    if (!name) return "Strategy name is missing";
    if (!Constant.nameRegexp.test(name)) return "Invalid character in strategy name";
    if (this.campaignType === CampaignType.SD && Constant.invalidSBSDNameRegexp.test(name)) {
      return "Invalid character in sponsored display strategy name";
    }
    return null;
  }

  protected validateState(entity: Partial<TStrategy>): string | null {
    if (entity.state === undefined || !StrategyStateEnum[entity.state]) return "Strategy state is not valid";
    return null;
  }

  protected validateAsinsInCatalog(entity: Partial<TStrategy>): Observable<string | null> {
    return this.catalog$.pipe(
      take(1),
      map((catalog) => {
        if (!entity.asins?.length) return "Asins are not valid";
        const missing = entity.asins.filter((a) => !catalog.asinOffers.has(a.asin)).map((a) => a.asin);
        if (missing.length > 0) return `The following ASINs are not in catalog: ${missing.join(", ")}`;
        return null;
      }),
    );
  }

  protected validateAsins(entity: Partial<TStrategy>): Observable<string | null> {
    return this.validateAsinsInCatalog(entity).pipe(
      switchMap((catalogError) => {
        if (catalogError) return of(catalogError);
        return combineLatest([
          this.tacosStrategiesService.unavailableAsinsForSponsoredAds$.pipe(take(1)),
          this.strategyIndex$.pipe(take(1)),
        ]).pipe(
          map(([tacosAsins, strategyIndex]) => {
            const asins = entity.asins ?? [];
            const existing = entity.strategyId ? strategyIndex.get(entity.strategyId) : undefined;
            const toCheck = existing ? asins.filter((a) => !existing.asins.find((sa) => sa.asin === a.asin)) : asins;
            if (toCheck.some((a) => tacosAsins.has(a.asin))) {
              return "Some ASINs are already in tacos strategies";
            }
            return null;
          }),
        );
      }),
    );
  }

  protected validateStrategyExists(entity: Partial<TStrategy>): Observable<string | null> {
    return this.strategyIndex$.pipe(
      take(1),
      map((strategyIndex) => {
        if (entity.strategyId === undefined || !strategyIndex.has(entity.strategyId)) return "Strategy does not exist";
        return null;
      }),
    );
  }

  private setProgressTotals(creationTotal: number, updateTotal: number): void {
    this.progress.next({
      completed: 0,
      total: creationTotal + updateTotal,
    });
  }

  private incrementProgress(): void {
    const current = this.progress.value;
    this.progress.next({ ...current, completed: current.completed + 1 });
  }

  private buildCreateRequests(
    creations: BulkUploadRowPreview<TStrategy>[],
  ): Observable<BulkUploadRunResultItem<TStrategy>>[] {
    return creations.map((creation) =>
      this.buildCreateRequest$(creation).pipe(
        take(1),
        catchError((error) => of(error)),
        map((strategyOrError: unknown) => this.mapStrategyToRunResultItem(strategyOrError, creation.parsedEntity)),
        tap(() => this.incrementProgress()),
      ),
    );
  }

  private buildUpdateRequests(
    updates: BulkUploadRowPreview<TStrategy>[],
  ): Observable<BulkUploadRunResultItem<TStrategy>>[] {
    return updates.map((update) =>
      this.strategyIndex$.pipe(
        take(1),
        withLatestFrom(this.accountSelectionService.singleAccountMarketplaceSelection$),
        switchMap(([strategyIndex, am]) => {
          const existingStrategy = strategyIndex.get(update.parsedEntity.strategyId!);
          if (!existingStrategy || !this.isStrategyResult(existingStrategy)) {
            return of("Strategy does not exist");
          }

          const body = this.getStrategyUpdateParams(update.parsedEntity, existingStrategy, {
            organizationId: am.resourceOrganizationId!,
            accountId: am.accountId,
            marketplace: am.marketplace,
          });
          const shouldUpdateAlgoMode = existingStrategy.algoMode !== update.parsedEntity.algoMode;

          const updateRequest$ = shouldUpdateAlgoMode
            ? this.strategyService.switchStrategyAlgoMode(
                am.accountId,
                am.marketplace,
                update.parsedEntity.strategyId!,
                update.parsedEntity.algoMode!,
                update.parsedEntity.acosTarget,
                update.parsedEntity.suggestedBid,
                update.parsedEntity.dailyBudget,
                update.parsedEntity.monthlyBudget,
              )
            : this.strategyService.updateStrategy(body);

          return updateRequest$.pipe(
            switchMap((strategy) => {
              if (!this.isStrategyResult(strategy)) return of(strategy);
              if (this.shouldUpdateMonthlyBudget(strategy, update.parsedEntity)) {
                const currentMonth = DateUtils.formatStringDateMonthForApi(strategy.today!);
                return this.strategyService.updateStrategyMonthlyBudget(
                  am.accountId,
                  am.marketplace,
                  strategy.strategyId!,
                  update.parsedEntity.monthlyBudget!,
                  update.parsedEntity.nextMonthlyBudget!,
                  currentMonth,
                );
              }
              return of(strategy);
            }),
            catchError((error) => of(error)),
          );
        }),
        map((strategyOrError: unknown) => this.mapStrategyToRunResultItem(strategyOrError, update.parsedEntity)),
        tap(() => this.incrementProgress()),
      ),
    );
  }

  private mapStrategyToRunResultItem(
    strategyOrError: unknown,
    parsedEntity: Partial<TStrategy>,
  ): BulkUploadRunResultItem<TStrategy> {
    const isCreation = parsedEntity.strategyId === 0;
    if (typeof strategyOrError === "string") {
      return {
        parsedEntity,
        resultEntity: null,
        status: BulkUploadRunStatus.FAILED,
        error: strategyOrError,
      };
    }
    if (Array.isArray(strategyOrError)) {
      return {
        parsedEntity,
        resultEntity: null,
        status: BulkUploadRunStatus.FAILED,
        error: strategyOrError.join("\n"),
      };
    }
    if (this.isStrategyResult(strategyOrError)) {
      return {
        parsedEntity,
        resultEntity: strategyOrError,
        status: isCreation ? BulkUploadRunStatus.CREATED : BulkUploadRunStatus.UPDATED,
      };
    }
    return {
      parsedEntity,
      resultEntity: null,
      status: BulkUploadRunStatus.FAILED,
    };
  }

  private isStrategyResult(value: unknown): value is TStrategy {
    return typeof value === "object" && value !== null && "strategyId" in value && "asins" in value;
  }

  private shouldUpdateMonthlyBudget(
    existingStrategy: Pick<Strategy, "algoMode" | "monthlyBudget" | "nextMonthlyBudget">,
    parsedEntity: Partial<TStrategy>,
  ): boolean {
    return !!(
      (existingStrategy.algoMode === AlgoMode.MONTHLY_BUDGET_TARGET &&
        parsedEntity.monthlyBudget &&
        parsedEntity.monthlyBudget !== existingStrategy.monthlyBudget) ||
      (parsedEntity.nextMonthlyBudget && parsedEntity.nextMonthlyBudget !== existingStrategy.nextMonthlyBudget)
    );
  }

  private getStrategyUpdateParams(
    parsedEntity: Partial<TStrategy>,
    existingStrategy: TStrategy,
    { organizationId, accountId, marketplace }: { organizationId: number; accountId: string; marketplace: Marketplace },
  ): AnyStrategyUpdateParams {
    const asinToAdd = parsedEntity.asins?.filter((a) => !existingStrategy.asins.find((sa) => sa.asin === a.asin)) ?? [];
    const asinToDelete =
      existingStrategy.asins.filter((a) => !parsedEntity.asins?.find((sa) => sa.asin === a.asin)) ?? [];
    return {
      ...parsedEntity,
      ...this.extraUpdateParams(parsedEntity, existingStrategy),
      accountId,
      marketplace,
      organizationId,
      strategyId: parsedEntity.strategyId!,
      asinsToAdd: asinToAdd.map((a) => a.asin),
      asinsToDelete: asinToDelete.map((a) => a.asin),
    };
  }

  private parseRow(fields: string[]): Observable<BulkUploadRowPreview<TStrategy> | null> {
    const config = this.bulkConfig;
    const partial = this.bulkImportService.parseCsvRow<TStrategy>(fields, this.campaignType);
    if (!partial) return of(null);

    const isCreation = partial.strategyId === 0;
    const fieldValidators = this.getFieldValidators();
    const creationValidators = this.getCreationFieldValidators();
    const updateValidators = this.getUpdateFieldValidators();

    return forkJoin(
      config.map((field) => {
        const error = fieldValidators[field.field]?.(partial) ?? null;
        const creationError = isCreation ? (creationValidators[field.field]?.(partial) ?? null) : null;
        const updateError = !isCreation ? (updateValidators[field.field]?.(partial) ?? null) : null;

        const errorAsObs = isObservable(error) ? error : of(error);
        const creationErrorAsObs = isObservable(creationError) ? creationError : of(creationError);
        const updateErrorAsObs = isObservable(updateError) ? updateError : of(updateError);

        return errorAsObs.pipe(
          switchMap((fieldError) => (fieldError ? of(fieldError) : isCreation ? creationErrorAsObs : updateErrorAsObs)),
        );
      }),
    ).pipe(
      withLatestFrom(this.strategyIndex$.pipe(take(1))),
      map(([errors, strategyIndex]) => {
        const existingFromIndex = strategyIndex.get(partial.strategyId!);
        const existingStrategy =
          existingFromIndex && this.isStrategyResult(existingFromIndex) ? existingFromIndex : undefined;
        const fieldsPreview: BulkUploadFieldPreview<TStrategy>[] = errors.map((error, i) => {
          const field = config[i].field;
          const parsedValue = partial[field];
          return {
            field,
            providedValue: fields[i],
            parsedValue: parsedValue!,
            error: error ?? undefined,
            isCreation,
            existingValue: isCreation || !existingStrategy ? undefined : existingStrategy[field],
            hasChanged:
              isCreation || !existingStrategy ? false : this.hasFieldChanged(field, existingStrategy, partial),
          };
        });
        return {
          parsedEntity: partial,
          fieldsPreview,
          status: this.getUploadRowPreviewStatus(partial, fieldsPreview),
        };
      }),
    );
  }

  private getUploadRowPreviewStatus(
    parsedEntity: Partial<TStrategy>,
    fieldsPreview: BulkUploadFieldPreview<TStrategy>[],
  ): BulkUploadPreviewStatus {
    if (rowHasFieldError(fieldsPreview)) return BulkUploadPreviewStatus.VALUE_ERROR;
    if (parsedEntity.strategyId === 0) return BulkUploadPreviewStatus.CREATION;
    if (rowIsUnchanged(fieldsPreview)) return BulkUploadPreviewStatus.UNCHANGED;
    return BulkUploadPreviewStatus.UPDATE;
  }

  private hasFieldChanged(field: keyof TStrategy, existing: TStrategy, parsed: Partial<TStrategy>): boolean {
    if (field === "asins") {
      return this.hasAsinsChanged(existing.asins, parsed.asins ?? []);
    }
    return existing[field] !== parsed[field];
  }

  private hasAsinsChanged(existingValue: StrategyAsin[], parsedValue: StrategyAsin[]): boolean {
    return (
      new Set(existingValue.map((a) => a.asin)).size !== new Set(parsedValue.map((a) => a.asin)).size ||
      existingValue.some((a) => !parsedValue.find((sa) => sa.asin === a.asin)) ||
      parsedValue.some((a) => !existingValue.find((sa) => sa.asin === a.asin))
    );
  }

  protected validateStateOnUpdate(entity: Partial<TStrategy>): Observable<string | null> {
    return this.validateTacosStrategyField(entity.strategyId!, "state", entity.state);
  }

  protected validateNameOnUpdate(entity: Partial<TStrategy>): Observable<string | null> {
    return this.validateTacosStrategyField(entity.strategyId!, "name", entity.name);
  }

  protected validateTacosStrategyField(
    strategyId: number,
    field: keyof StrategyFromCT<CampaignType>,
    value: TStrategy[keyof TStrategy] | undefined,
  ): Observable<string | null> {
    return this.strategyIndex$.pipe(
      take(1),
      map((strategyIndex) => {
        const strategy = strategyIndex.get(strategyId);
        if (!strategy) return null;
        if (strategy.algoMode === AlgoMode.TACOS_TARGET && strategy[field] !== value)
          return "Tacos Strategies " + field + " cannot be updated from " + this.campaignType + " bulk upload";
        return null;
      }),
    );
  }
}
